import * as React from 'react';
import { OTPInput, OTPInputContext } from 'input-otp';
import { Minus } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * shadcn/ui-style OTP input wrapper around `input-otp` (1.4). Three
 * pieces:
 *   <InputOTP> — the controller (handles paste / keyboard / focus)
 *   <InputOTPGroup> — visually groups slots (we use one group of 6)
 *   <InputOTPSlot index={n} /> — renders slot n with the right
 *     border/active/caret styling
 *   <InputOTPSeparator /> — optional `–` between groups (unused for
 *     our 6-digit Fluxter codes; kept for parity)
 *
 * Slot styling intentionally matches the rest of the auth surfaces
 * (`border-input`, `bg-background`, `ring-ring/50`) rather than the
 * shadcn defaults so the OTP cells feel like a continuation of the
 * email field one screen earlier.
 */

function InputOTP({
  className,
  containerClassName,
  ...props
}: React.ComponentProps<typeof OTPInput> & { containerClassName?: string }) {
  return (
    <OTPInput
      data-slot="input-otp"
      containerClassName={cn(
        'flex items-center gap-2 has-[:disabled]:opacity-50',
        containerClassName,
      )}
      className={cn('disabled:cursor-not-allowed', className)}
      {...props}
    />
  );
}

function InputOTPGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="input-otp-group"
      className={cn('flex items-center gap-1.5', className)}
      {...props}
    />
  );
}

function InputOTPSlot({
  index,
  className,
  ...props
}: React.ComponentProps<'div'> & { index: number }) {
  const inputOTPContext = React.useContext(OTPInputContext);
  const slot = inputOTPContext.slots[index];
  const { char = '', hasFakeCaret = false, isActive = false } = slot ?? {};

  return (
    <div
      data-slot="input-otp-slot"
      data-active={isActive}
      className={cn(
        'border-input relative flex h-11 w-9 items-center justify-center rounded-md border bg-background text-[15px] font-medium transition-colors',
        'data-[active=true]:border-ring data-[active=true]:ring-3 data-[active=true]:ring-ring/30',
        className,
      )}
      {...props}
    >
      {char}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-4 w-px animate-pulse bg-foreground" />
        </div>
      )}
    </div>
  );
}

function InputOTPSeparator({ ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="input-otp-separator" role="separator" {...props}>
      <Minus className="size-3.5" />
    </div>
  );
}

export { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator };
